<!-- uebungen-library-print-uebung-modal.vue -->
<template>
  <div class="uebungen-library__modal flex flex-col fixed top-0 left-0 w-screen h-screen z-[1000]">
    <div
      class="uebungen-library__modal-header relative flex flex-row justify-center items-center px-20 bg-primary-900 text-white transition-all delay-75">
      <button @click="closeModal"
        class="flex flex-row items-center py-3 px-8 hover:text-secondary_color transition-all">
        <hermeneus-icon name="hi-uebungen-new" color="current" height="48" width="48" class="mr-2"></hermeneus-icon>
        <span class="text-xl font-light md-minus:hidden">Übungsbibliothek</span>
      </button>
      <i class="fas fa-angle-right mx-5"></i>
      <button @click="loadComponentInModal('uebungen-library-print-uebung', 'Übung drucken', 'hi-uebung-print')"
        :class="{ 'bg-primary-800': component_alias === 'uebungen-library-print-uebung', 'bg-primary-900': component_alias !== 'uebungen-library-print-uebung' }"
        class="flex flex-row items-center bg-primary-800 py-3 px-8 pointer-events-none transition-all">
        <hermeneus-icon name="hi-uebung-print" color="current" height="48" width="48"
          class="mr-2 pt-2"></hermeneus-icon>
        <span class="text-xl font-light md-minus:hidden">Übung drucken</span>
      </button>
      <!-- DATEN DER AKTUELLEN KOMPONENTE WERDEN HIER IM HEADER ANGEZEIGT -->
      <div v-if="component_alias && (component_alias !== 'uebungen-library-print-uebung')"
        class="flex flex-row items-center pointer-events-none">
        <i class="fas fa-angle-right mx-5"></i>
        <button
          class="flex flex-row items-center py-3 px-8 bg-white text-primary-700  transition-all">
          <hermeneus-icon :name="component_icon" color="current" height="48" width="48" class="mr-2"></hermeneus-icon>
          <span class="text-xl md-minus:hidden">{{ component_title }}</span>
        </button>
      </div>
      <button class="absolute right-8 top-1/2 transform -translate-y-1/2 text-primary-400 hover:text-white" @click="closeModal"><i class="text-3xl fas fa-times"></i></button>
    </div>
    <div class="uebungen-library__modal-content flex-grow overflow-auto">
      <transition name="fade" mode="out-in">
        <div v-if="isLoading" class="pt-32 flex justify-center items-center">
          <loading-comet class="text-primary-700"></loading-comet>
        </div>
        <div v-else-if="dynamicComponent">
          <div class="uebungen-library__print-options p-6 flex justify-end"><Button label="Drucken" icon="bx bx-printer" size="large"
              @click="print"></Button> </div>
          <component :is="dynamicComponent" :data="component_data" class="p-5 bg-white"></component>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { EventBus } from "../../hermeneus.eventbus/eventbus.js";
import { haxiosAPIClient } from "@/haxios/haxios.js";
import LoadingComet from "@/loader/LoadingComet.vue";

export default {
  name: "uebungen-library-print-uebung-modal",
  props: {
    uebung: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isClosing: false,
      isLoading: false,
      component_alias: null,
      component_title: null,
      component_icon: null,
      component_data: null
    };
  },
  computed: {
    resolvedUebung() {
      return this.$resolveUebungFromRegistry(this.uebung.uebung_type);
    },
    dynamicComponent() {
      return this.component_alias || 'uebungen-library-print-uebung';
    }
  },
  methods: {
    closeModal() {
      this.isClosing = true;
      setTimeout(() => {
        this.$emit('close-print-uebung-modal');
        this.isClosing = false;
      }, 300);
    },
    print() {
      this.prepareForPrint();
      // Erzwungener Reflow, damit Chrome die Styles übernimmt
      this.$el.offsetHeight;
      window.print();
    },
    /**
     * Liest aus der UebungenRegistry die Komponenteninformationen aus, die in der
     * config.json definiert sind.
     */
    getPrintComponent() {
      this.component_alias = this.$resolveUebungFromRegistry(this.uebung.uebung_type).components.print?.alias || 'uebungen-library-print-uebung';
      this.component_title = this.$resolveUebungFromRegistry(this.uebung.uebung_type).components.print?.title || 'Übung drucken';
      this.component_icon = this.$resolveUebungFromRegistry(this.uebung.uebung_type).appearance.icon ?? 'hi-uebung-print';
      this.getPrintComponentData();
    },
    getPrintComponentData() {
      this.isLoading = true;
      haxiosAPIClient.get(`/uebungen/print/${this.uebung.id}`).then(response => {
        this.component_data = response.data.data;
        this.isLoading = false;
      });
    },
    loadComponentInModal(component_alias, component_title, component_icon, component_data) {
      this.component_alias = component_alias;
      this.component_title = component_title;
      this.component_icon = component_icon;
      this.component_data = component_data;
    },
    handleEscKey(event) {
      if (event.key === 'Escape') {
        this.closeModal();
      }
    },
    preventScroll() {
      document.body.style.overflow = 'hidden';
    },
    enableScroll() {
      document.body.style.overflow = '';
    },
    prepareForPrint() {
      const modal = this.$el;
      const content = modal.querySelector('.uebungen-library__modal-content');
      const header = modal.querySelector('.uebungen-library__modal-header');
      const printOptions = modal.querySelector('.uebungen-library__print-options');

      // Tailwind-Klassen entfernen, die das Layout beschränken
      modal.classList.remove('fixed', 'h-screen', 'w-screen', 'flex', 'flex-col');
      modal.style.setProperty('position', 'static', 'important');
      modal.style.setProperty('height', 'auto', 'important');
      modal.style.setProperty('width', '100%', 'important');
      modal.style.setProperty('display', 'block', 'important');

      // Content: Scroll-Position zurücksetzen und Overflow sichtbar machen
      if (content) {
        content.scrollTop = 0;
        content.classList.remove('overflow-auto', 'flex-grow');
        content.style.setProperty('overflow', 'visible', 'important');
        content.style.setProperty('height', 'auto', 'important');
      }

      // Header und Drucken-Button ausblenden
      if (header) header.style.setProperty('display', 'none', 'important');
      if (printOptions) printOptions.style.setProperty('display', 'none', 'important');

      // Geschwister-Elemente (Dashboard, Overlay) ausblenden
      const parent = modal.parentElement;
      if (parent) {
        Array.from(parent.children).forEach(sibling => {
          if (sibling !== modal) {
            sibling._savedDisplay = sibling.style.display;
            sibling.style.setProperty('display', 'none', 'important');
          }
        });
        parent.style.setProperty('overflow', 'visible', 'important');
        parent.style.setProperty('height', 'auto', 'important');
      }
    },
    restoreFromPrint() {
      const modal = this.$el;
      const content = modal.querySelector('.uebungen-library__modal-content');
      const header = modal.querySelector('.uebungen-library__modal-header');
      const printOptions = modal.querySelector('.uebungen-library__print-options');

      // Tailwind-Klassen wiederherstellen
      modal.classList.add('fixed', 'h-screen', 'w-screen', 'flex', 'flex-col');
      modal.style.removeProperty('position');
      modal.style.removeProperty('height');
      modal.style.removeProperty('width');
      modal.style.removeProperty('display');

      if (content) {
        content.classList.add('overflow-auto', 'flex-grow');
        content.style.removeProperty('overflow');
        content.style.removeProperty('height');
      }

      if (header) header.style.removeProperty('display');
      if (printOptions) printOptions.style.removeProperty('display');

      const parent = modal.parentElement;
      if (parent) {
        Array.from(parent.children).forEach(sibling => {
          if (sibling !== modal) {
            sibling.style.display = sibling._savedDisplay || '';
          }
        });
        parent.style.removeProperty('overflow');
        parent.style.removeProperty('height');
      }
    }
  },
  mounted() {
    this.getPrintComponent();
    // Hintergrund-Scrolling deaktivieren
    this.preventScroll();

    // Event-Listener für ESC-Taste und Drucken hinzufügen
    window.addEventListener('keydown', this.handleEscKey);
    window.addEventListener('beforeprint', this.prepareForPrint);
    window.addEventListener('afterprint', this.restoreFromPrint);

    // Event-Listener für das Laden von Komponenten im Modal
    EventBus.$on('uebungen-library__load-component-in-print-modal', ComponentData => {
      this.loadComponentInModal(ComponentData.component_alias, ComponentData.component_title, ComponentData.component_icon);
    });
  },
  beforeDestroy() {
    // Hintergrund-Scrolling wieder aktivieren
    this.enableScroll();

    // Event-Listener entfernen
    window.removeEventListener('keydown', this.handleEscKey);
    window.removeEventListener('beforeprint', this.prepareForPrint);
    window.removeEventListener('afterprint', this.restoreFromPrint);
    EventBus.$off('uebungen-library__load-component-in-print-modal');
  }
}
</script>


<style scoped>

.uebungen-library__modal {
  animation: fadeIn 0.3s ease;
}

.uebungen-library__modal__backdrop {
  transition: opacity 0.3s ease;
}

.uebungen-library__modal__content {
  animation: slideIn 0.3s ease;
  max-height: 100vh;
}

.uebungen-library__modal__backdrop.closing {
  opacity: 0;
}

.uebungen-library__modal__content.closing {
  animation: slideOut 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(50px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateY(0);
    opacity: 1;
  }

  to {
    transform: translateY(50px);
    opacity: 0;
  }
}

.uebungen-library__modal-header {
  opacity: 0;
  animation: fadeIn 0.5s ease-in-out forwards;
  animation-delay: 500ms;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.uebungen-library__modal-content {
  overflow-y: auto;
}
</style>

<style>
@media print {
  /* Fallback-Regeln (primäre Logik in prepareForPrint/restoreFromPrint) */
  .uebungen-library__modal-header,
  .uebungen-library__print-options {
    display: none !important;
  }

  .uebungen-library__modal {
    position: static !important;
    width: 100% !important;
    height: auto !important;
  }

  .uebungen-library__modal-content {
    overflow: visible !important;
  }
}
</style>
